import React, { useState, useEffect } from 'react'
import { List } from 'immutable'
import RowV3 from '../../Row.v3/index'
import ChevronDownIcon from '../../Icon/icons/ChevronDown/index'
import ReplayIcon from '../../Icon/icons/Replay/index'
import CheckIcon from '../../Icon/icons/Check/index'
import Button, { TYPES } from '../../Button/index'
import { COURSE_STATE_IN_PROGRESS, COURSE_STATE_COMPLETE } from '../../../services/detail/index'
import DetailPageV3CourseModuleItem from './DetailPageV3CourseModuleItem'
import DetailPageV3CourseModuleAsset from './DetailPageV3CourseModuleAsset'
import DetailPageV3CourseModulePlaceholder from './DetailPageV3CourseModulePlaceholder'

const MAX_MODULE_ITEMS_TO_SHOW = 3

/**
 * @param {Object} props - The props
 * @param {string} props.moduleState - The course module state
 * @param {List} props.moduleItemsData - The course module items data
 * @param {boolean} props.isPublished - Whether the module is published
 * @param {List} props.moduleAssets - The module assets
 * @param {Map} props.staticText - The static text data
 * @param {number} props.courseContentId - The course content ID
 * @param {function} props.onCourseStateChange - The function to handle course state change
 * @param {function} props.handleStateChange - The function to handle state change
 * @param {boolean} props.showProgressButton - Whether to show the progress button
 * @param {boolean} props.isButtonDisabled - Whether the button is disabled
 * @returns {JSX.Element} DetailPageV3CourseModuleContent
 */
const DetailPageV3CourseModuleContent = (props) => {
  const {
    moduleState,
    moduleItemsData = List(),
    isPublished,
    moduleAssets = List(),
    staticText,
    courseContentId,
    onCourseStateChange,
    handleStateChange,
    showProgressButton,
    isButtonDisabled,
  } = props

  const contentCount = moduleItemsData.size + moduleAssets.size
  const shouldTruncate = contentCount > MAX_MODULE_ITEMS_TO_SHOW
    && moduleState !== COURSE_STATE_IN_PROGRESS
  const completed = moduleState === COURSE_STATE_COMPLETE

  const [expanded, setExpanded] = useState(!shouldTruncate)

  const visibleModuleItemsData = shouldTruncate && !expanded
    ? moduleItemsData.take(MAX_MODULE_ITEMS_TO_SHOW)
    : moduleItemsData

  useEffect(() => {
    if (moduleState === COURSE_STATE_IN_PROGRESS) {
      setExpanded(true)
    }
  }, [moduleState])

  return (
    <>
      <div className="detail-page-v3__course-module-tiles">
        {visibleModuleItemsData.size > 0 && isPublished ? (
          <RowV3 asGrid={true}>
            {visibleModuleItemsData.map((itemData, index) => (
              <DetailPageV3CourseModuleItem
                key={index}
                itemData={itemData}
                index={index}
                courseContentId={courseContentId}
                onCourseStateChange={onCourseStateChange}
              />
            ))}
          </RowV3>
        ) : (
          <RowV3 asGrid={true}>
            <DetailPageV3CourseModulePlaceholder />
          </RowV3>
        )}
      </div>
      {moduleAssets.size > 0 && expanded && (
        <div className="detail-page-v3__course-module-assets">
          <div className="detail-page-v3__course-module-assets-title">
            <span>{staticText.get('additionalMaterials')}</span>
          </div>
          {moduleAssets.map((asset) => (
            <DetailPageV3CourseModuleAsset
              key={asset.get('uuid')}
              asset={asset}
              courseContentId={courseContentId}
              onCourseStateChange={onCourseStateChange}
            />
          ))}
        </div>
      )}
      {expanded && showProgressButton && (
        <Button
          className="detail-page-v3__course-module-button"
          onClick={handleStateChange}
          disabled={isButtonDisabled}
        >
          {completed ? <ReplayIcon size={20} /> : <CheckIcon size={20} />}
          {' '}
          <span className="detail-page-v3__course-module-button-label">
            {completed ? staticText.get('startAgain') : staticText.get('markAsComplete')}
          </span>
        </Button>
      )}
      {!expanded && (
        <div className="detail-page-v3__course-module-content-footer">
          <ChevronDownIcon />
          <Button
            type={TYPES.LINK}
            onClick={() => setExpanded(true)}
            className="detail-page-v3__course-module-content-footer-button"
          >
            {staticText.get('showAllContent')} {(`${contentCount}`)}
          </Button>
          <ChevronDownIcon />
        </div>
      )}
    </>
  )
}

export default DetailPageV3CourseModuleContent
